import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/storage/storage";
import http from "../utils/http-common";

export function useListStorages() {
  return useQuery({
    queryKey: ["storages"],
    queryFn: api.listStorages,
    staleTime: 5 * 60_000,
    cacheTime: 10 * 60_000,
    refetchOnWindowFocus: true,
    // No cloud provider configured -> /storage returns 500. That's a stable
    // state, not a transient error, so don't retry (settle fast for the UI gate).
    retry: false,
  });
}

export function useListObjects(bucket?: string, prefix = "") {
  return useQuery({
    queryKey: ["objects", bucket, prefix],
    queryFn: () => api.listObjects(bucket!, prefix),
    enabled: Boolean(bucket),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    refetchInterval: 10_000,
  });
}

export function useSearchObjects(bucket?: string, prefix = "", term = "") {
  return useQuery({
    queryKey: ["search", bucket, prefix, term],
    queryFn: () => api.searchObjects(bucket!, prefix, term),
    enabled: Boolean(bucket) && term.length > 0,
    keepPreviousData: true,
  });
}

export function useGenerateSignedUrl() {
  return useMutation({
    mutationFn: ({
      bucket,
      path,
      method = "GET",
      expiration = 600,
      contentType,
      disposition,
      generation,
    }: {
      bucket: string;
      path: string;
      method?: string;
      expiration?: number;
      contentType?: string;
      disposition?: string;
      generation?: string;
    }) =>
      api.generateSignedUrl(
        bucket,
        path,
        method,
        expiration,
        contentType,
        disposition,
        generation
      ),
  });
}

export function useUploadObject(bucket?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      prefix = "",
      meta_data = {},
    }: {
      file: File;
      prefix?: string;
      meta_data?: Record<string, any>;
    }) => api.uploadObject(bucket!, file, prefix, meta_data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["objects", bucket, variables?.prefix ?? ""],
      });
    },
  });
}

export function useDeleteObject(bucket?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ path }: { path: string }) => api.deleteObject(bucket!, path),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["objects", bucket],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["artifact_tree", bucket],
        exact: false,
      });
    },
  });
}

export function useGenerateUploadUrl() {
  return useMutation({
    mutationFn: ({
      bucket,
      path,
      expiration = 600,
    }: {
      bucket: string;
      path: string;
      expiration?: number;
    }) => api.generateUploadSignedUrl(bucket, path, expiration),
  });
}

export function useArtifactTree(bucket?: string) {
  return useQuery({
    queryKey: ["artifact_tree", bucket],
    queryFn: async () => api.getArtifactTree(bucket!),
    enabled: Boolean(bucket),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    refetchInterval: 10_000,
  });
}

export function useSyncBucket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bucket: string) => api.syncBucket(bucket),
    onSuccess: (_data, bucket) => {
      queryClient.invalidateQueries({ queryKey: ["objects", bucket] });
      queryClient.invalidateQueries({ queryKey: ["artifact_tree", bucket] });
    },
  });
}

export function useSearchObjectsPaginated(
  bucket,
  prefix = "",
  term = "",
  page = 0,
  pageSize = 25
) {
  return useQuery({
    queryKey: ["search", bucket, prefix, term, page, pageSize],
    queryFn: () =>
      api.searchObjectsPaginated(bucket, prefix, term, page + 1, pageSize),
    enabled: Boolean(bucket),
    keepPreviousData: true,
  });
}
export function useSearchTreeObjects(bucket, currentPath, term, options) {
  return useQuery({
    queryKey: ["search-tree-objects", bucket, currentPath, term],
    queryFn: () => api.fetchSearchTreeObjects(bucket, currentPath, term),
    ...options,
  });
}
